import CharacterFilmPanel from './CharacterFilmPanel';
import FlowkitDashboardTab from './FlowkitDashboardTab';
import UgcFlowkitPipelinePanel from './UgcFlowkitPipelinePanel';
import type { CSSProperties } from 'react';
import { useEffect } from 'react';
import { useStore, type Tab } from '../services/store';
import { subscribeNodeGraphEvents } from '../services/nodeGraphEvents';
import {
  Activity,
  Code,
  Download,
  Film,
  GitBranch,
  LogOut,
  MessageSquare,
  Music2,
  Send,
  Sparkles,
  Users,
  Wand2,
  type LucideIcon,
} from 'lucide-react';
import ChatInterface from './ChatInterface';
import CodeUpdate from './CodeUpdate';
import MasterPromptTab from './MasterPromptTab';
import FlowPanel from './FlowPanel';
import NormalPanelTab from './NormalPanelTab';
import AgentActivityGraph from './AgentActivityGraph';
import UsersTab from './UsersTab';
import { TikTokUploadPanel } from './TikTokUploadPanel';
import { YouweeResearchHubTab } from './YouweeResearchHubTab';
import PublishingApprovalPanel from './PublishingApprovalPanel';

const tabs: { id: Tab; label: string; description: string; icon: LucideIcon; accent: string; adminOnly?: boolean }[] = [
  { id: 'masterPrompt',     label: 'UGC Master',          description: 'Batch video prompts and scripts',         icon: Wand2,         accent: 'var(--studio-chili)' },
  { id: 'youwee',           label: 'Youwee / Research',   description: 'Đa kênh · Video đối thủ · Review kênh mình & KBS', icon: Download, accent: 'var(--studio-terracotta)' },
  { id: 'characterFilms',   label: 'Film Characters',     description: 'Screenplay and episodic character films', icon: Film,          accent: 'var(--studio-saffron)' },
  { id: 'ugcFlowkit',       label: 'UGC flowkit',         description: 'Current product pipeline and delivery state', icon: Sparkles,  accent: 'var(--studio-gold)' },
  { id: 'flowkitDashboard', label: 'Flowkit Dashboard',   description: 'Native Flowkit operations console',       icon: Activity,      accent: 'var(--studio-terracotta)' },
  { id: 'flowPanel',        label: 'FlowBoard Dashboard', description: 'Embedded FlowBoard media production app', icon: GitBranch,     accent: 'var(--studio-saffron)' },
  { id: 'publishing',       label: 'Duyệt đăng bài',      description: 'Approve video, caption, link and slot before the Pixel posts', icon: Send, accent: 'var(--studio-gold)' },
  { id: 'code',             label: 'Code update',         description: 'Codex mobile control surface',            icon: Code,          accent: 'var(--studio-plum)' },
  { id: 'users',            label: 'User',                description: 'Manage studio user credentials',          icon: Users,         accent: 'var(--studio-chili)', adminOnly: true },
];

export default function Layout() {
  const { activeTab, setActiveTab, user, logout } = useStore();
  const visibleTabs = tabs.filter(t => !t.adminOnly || user?.role === 'admin');
  const active = tabs.find(tab => tab.id === activeTab) ?? tabs[0];
  const ActiveIcon = active.icon;

  // ── Global job listener — shares the page's authenticated graph feed ──
  useEffect(() => {
    // Helper: add a job to the store and ACK it from the server
    const addJob = (p: {
      jobId: string; script: string; idea?: string;
      settings?: Record<string, any>;
      jobImageUrls?: string[]; jobImageFiles?: string[];
      autoProcess?: boolean;
      autoGenerate?: boolean;
      autoMerge?: boolean;
      studioJobId?: string;
      serverBoardId?: number;   // set by server-side execution — skip findExistingBoard
      serverShots?: Array<{ shotNumber: number; prompt: string; voiceover: string; durationSec: number }>;
    }) => {
      const store = useStore.getState();
      const existingJob = store.mpJobs.find(job => job.id === p.jobId);
      if (existingJob) {
        store.updateMpJob(p.jobId, {
          studioJobId: p.studioJobId,
          autoMerge: p.autoMerge !== false,
          jobImageUrls: p.jobImageUrls || [],
          jobImageFiles: p.jobImageFiles || [],
        });
        store.shots
          .filter(shot => shot.jobId === p.jobId && shot.status !== 'done')
          .forEach(shot => store.updateShot(shot.id, {
            selected: true,
            imageDataUrls: [...(p.jobImageUrls || [])],
            imageFiles: [...(p.jobImageFiles || [])],
            status: 'pending',
            errorMsg: undefined,
            fkSceneId: undefined,
            fkImageRequestId: undefined,
            fkVideoRequestId: undefined,
          }));
        store.updateMpJob(p.jobId, {
          fkVaEntityIds: undefined,
          fkVaEntityNames: undefined,
          fkVaMediaIds: undefined,
          fkProjectId: undefined,
          fkVideoId: undefined,
        });
        store.setAutoMerge(p.autoMerge !== false);
        if (p.autoGenerate !== false) store.queueAutoGenerateJob(p.jobId);
        store.setProcessingJobId(p.jobId);
        store.setActiveTab('normalPanel');
        return;
      }

      const s = p.settings || {};
      if (s.cliProvider) store.setMpCliProvider(s.cliProvider);
      if (s.aspect)      store.setMpAspect(s.aspect);
      if (s.quality)     store.setMpQuality(s.quality);
      if (s.duration)    store.setMpDuration(s.duration);
      if (s.language)    store.setMpLanguage(s.language);
      if (s.ugcMode)     store.setMpUgcMode(s.ugcMode);
      if (s.engine === 'flowboard' || s.engine === 'flowkit') store.setMpEngine(s.engine);
      if (s.characterName)  store.setMpCharacterName(s.characterName);
      if (s.characterDesc)  store.setMpCharacterDesc(s.characterDesc);
      if (s.characterImageUrl && s.characterImageFile)
        store.setMpCharacterImage(s.characterImageUrl, s.characterImageFile);
      store.setMpVideoMode('script');
      const serverShots = Array.isArray(p.serverShots) && p.serverShots.length > 0 ? p.serverShots : [];

      if (p.autoProcess !== false) {
        store.setAutoMode(true);
        if (serverShots.length > 0) {
          store.setMpEngine('flowkit');
          store.setAutoMerge(p.autoMerge !== false);
          if (p.autoGenerate !== false) store.queueAutoGenerateJob(p.jobId);
        }
        else store.setAutoProcessPending(true);
      }

      store.addMpJob({
        id: p.jobId,
        studioJobId: p.studioJobId,
        autoMerge: p.autoMerge !== false,
        idea: p.idea || p.script.split(/[\n.!?]/)[0].trim().slice(0, 120) || 'n8n Job',
        technique: s.technique || 'UGC',
        material: s.material || 'Live Action',
        duration: s.duration || store.mpDuration,
        language: s.language || store.mpLanguage,
        aspect: s.aspect || store.mpAspect,
        progress: 100,
        status: 'done',
        script: p.script,
        ...(serverShots.length > 0 ? { shotsCreated: true } : {}),
        category: s.category || 'General',
        jobImageUrls: p.jobImageUrls || [],
        jobImageFiles: p.jobImageFiles || [],
        ...(p.serverBoardId ? { boardId: p.serverBoardId } : {}),
      });

      if (serverShots.length > 0) {
        store.addShots(serverShots.map(shot => ({
          id: crypto.randomUUID(),
          jobId: p.jobId,
          shotNumber: shot.shotNumber,
          prompt: shot.prompt,
          voiceover: shot.voiceover,
          durationSec: shot.durationSec,
          selected: true,
          imageDataUrls: [...(p.jobImageUrls || [])],
          imageFiles: [...(p.jobImageFiles || [])],
          status: 'pending' as const,
        })));
        store.setProcessingJobId(p.jobId);
        store.setActiveTab('normalPanel');
      } else {
        store.setActiveTab('masterPrompt');
      }
    };

    // 1. Fetch any pending jobs from server (in case browser was closed when they arrived)
    fetch('/api/n8n/studio/pending-jobs')
      .then(r => r.json())
      .then(({ jobs }) => { if (Array.isArray(jobs)) jobs.forEach(addJob); })
      .catch(() => {});

    // 2. Real-time jobs only; reconnect does not replay/reset pending jobs.
    return subscribeNodeGraphEvents(data => {
      if (data.type === 'master-prompt-job') addJob(data.payload);
    });
  }, []);

  return (
    <div className="studio-shell h-screen overflow-hidden">
      <div className="studio-app-frame">
        <aside className="studio-sidebar">
          <div className="studio-brand">
            <div className="studio-logo-wrap">
              <img src="/assets/ddlab-logo.webp" alt="" className="h-full w-full object-contain" />
            </div>
            <div className="min-w-0">
              <div className="studio-brand-title">DDStudio</div>
              <div className="studio-brand-subtitle">studio.ddlab.cloud</div>
            </div>
          </div>

          <div className="factory-rail h-1 w-full rounded-full" />

          <nav className="studio-nav" aria-label="Studio navigation">
            {visibleTabs.map(tab => {
              const Icon = tab.icon;
              const selected = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`studio-nav-button ${selected ? 'active' : ''}`}
                  style={selected ? { '--studio-nav-accent': tab.accent } as CSSProperties : undefined}
                >
                  <span className="studio-nav-icon" style={{ color: selected ? tab.accent : undefined }}>
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 text-left">
                    <span className="studio-nav-label">{tab.label}</span>
                    <span className="studio-nav-description">{tab.description}</span>
                  </span>
                </button>
              );
            })}
          </nav>

          <div className="studio-sidebar-footer">
            <div className="studio-user-chip">
              <span className="studio-user-avatar">{(user?.username ?? 'S').slice(0, 1).toUpperCase()}</span>
              <span className="min-w-0">
                <span className="studio-user-label">Signed in</span>
                <span className="studio-user-name">{user?.username}</span>
              </span>
            </div>
            <button onClick={logout} className="studio-icon-button" title="Sign out">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </aside>

        <section className="studio-workspace">
          <header className="studio-workspace-header">
            <div className="flex min-w-0 items-center gap-3">
              <div className="studio-section-icon" style={{ color: active.accent }}>
                <ActiveIcon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h1 className="studio-section-title">{active.label}</h1>
                <p className="studio-section-description">{active.description}</p>
              </div>
            </div>

            <div className="studio-status-row">
              <span className="studio-status-pill">
                <span className="studio-status-dot online" />
                MCP online
              </span>
              <span className="studio-status-pill accent">
                <Sparkles className="h-3.5 w-3.5" />
                AI ready
              </span>
            </div>
          </header>

          <main className="studio-content" style={{ position: 'relative' }}>
            {activeTab === 'chat' && <ChatInterface />}
            {activeTab === 'masterPrompt' && <MasterPromptTab />}
            {activeTab === 'characterFilms' && <CharacterFilmPanel onBackToUgc={() => { useStore.getState().setMpUgcMode('faceless'); setActiveTab('masterPrompt'); }} />}
            {activeTab === 'ugcFlowkit' && <UgcFlowkitPipelinePanel />}
            {activeTab === 'flowkitDashboard' && <FlowkitDashboardTab />}
            {activeTab === 'normalPanel' && <NormalPanelTab />}
            {activeTab === 'flowPanel' && <FlowPanel />}
            {activeTab === 'tiktok' && <TikTokUploadPanel />}
            {activeTab === 'youwee' && <YouweeResearchHubTab />}
            {activeTab === 'publishing' && <PublishingApprovalPanel />}
            {/* AgentActivityGraph: always mounted so its event subscription stays alive
                and captures events fired from ANY tab (Youwee research, chat, etc.) */}
            <div
              style={{
                position: activeTab === 'agentGraph' ? 'absolute' : 'fixed',
                top: 0,
                left: 0,
                width: activeTab === 'agentGraph' ? '100%' : 0,
                height: activeTab === 'agentGraph' ? '100%' : 0,
                overflow: 'hidden',
                opacity: activeTab === 'agentGraph' ? 1 : 0,
                visibility: activeTab === 'agentGraph' ? 'visible' : 'hidden',
                pointerEvents: activeTab === 'agentGraph' ? 'auto' : 'none',
                zIndex: activeTab === 'agentGraph' ? 2 : -1,
              }}
            >
              <AgentActivityGraph />
            </div>
            {activeTab === 'code' && <CodeUpdate />}
            {activeTab === 'users' && <UsersTab />}
          </main>
        </section>
      </div>
    </div>
  );
}
